import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  FolderKanban,
  Package,
  Target,
  Sparkles,
  LogOut,
  Globe2,
  ChevronRight,
  ChevronDown,
  Check,
  Plus,
  Menu,
  X,
  Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useCountry } from "@/lib/CountryContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/companies", label: "Companies", icon: Building2, testid: "nav-companies" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/catalog", label: "Catalog", icon: Package, testid: "nav-catalog" },
  { to: "/opportunities", label: "Opportunities", icon: Target, testid: "nav-opportunities" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", adminOnly: true },
];

function CountrySelector() {
  const { country, countries, change, activate } = useCountry();
  const [open, setOpen] = useState(false);
  const [activating, setActivating] = useState(null);

  const current = countries.find((c) => c.code === country) || { code: country, name: country };

  const handleClick = async (c) => {
    if (c.active) {
      change(c.code);
      setOpen(false);
      return;
    }
    if (!window.confirm(
      `Activate ${c.name}? This will seed the database with ${c.code} companies and projects.`
    )) return;
    try {
      setActivating(c.code);
      const r = await activate(c.code);
      toast.success(
        r.companies_added !== undefined
          ? `${c.name} activated · ${r.companies_added} companies, ${r.projects_added} projects seeded`
          : `${c.name} activated`
      );
      setOpen(false);
    } catch (e) {
      toast.error("Activation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setActivating(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="country-selector"
        className="flex items-center gap-2 px-2 sm:px-3 py-1.5 border border-gray-200 hover:border-[#004B87] transition-colors text-sm"
      >
        <Globe2 size={13} className="text-[#004B87]" />
        <span className="font-bold">{current.code}</span>
        <span className="hidden sm:inline text-xs text-gray-500">
          {current.name.replace("United Arab Emirates", "UAE").replace("Saudi Arabia", "KSA")}
        </span>
        <ChevronDown size={12} className="text-gray-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 shadow-lg z-40" data-testid="country-dropdown">
            <div className="px-3 py-2 border-b border-gray-200">
              <div className="label-eyebrow">Region</div>
            </div>
            {countries.map((c) => (
              <button
                key={c.code}
                onClick={() => handleClick(c)}
                disabled={activating === c.code}
                data-testid={`country-option-${c.code}`}
                className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                  c.code === country ? "bg-[#004B87]/5" : ""
                }`}
              >
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {c.code === country && <Check size={12} className="text-[#004B87]" />}
                    {c.code}
                  </div>
                  <div className="text-xs text-gray-500">{c.name}</div>
                </div>
                {c.active ? (
                  <span className="chip border-[#059669] text-[#059669] bg-emerald-50">active</span>
                ) : activating === c.code ? (
                  <span className="text-xs text-gray-400">activating…</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-[#004B87] font-bold">
                    <Plus size={11} /> activate
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SidebarBody({ onNavClick }) {
  const { user, logout } = useAuth();
  const { country } = useCountry();
  return (
    <>
      <div className="px-5 py-6 border-b border-gray-200">
        <div className="label-eyebrow mb-1">Sodimate</div>
        <div className="font-display text-2xl font-bold tracking-tight" data-testid="brand-title">
          {country} / BD
        </div>
        <div className="mt-1 text-[11px] text-gray-500 font-medium">
          Business Development
        </div>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavClick}
            className={({ isActive }) =>
              `nav-item ${isActive ? "active" : ""}`
            }
          >
            <item.icon size={16} strokeWidth={1.8} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#004B87] text-white grid place-items-center font-bold text-sm">
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">
              {user?.role}
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full text-left text-sm text-gray-600 hover:text-red-600 flex items-center gap-2 py-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  const { country } = useCountry();
  const location = useLocation();
  const current = NAV.find((n) => location.pathname.startsWith(n.to));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-white" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-gray-200 bg-white flex-col" data-testid="sidebar">
        <SidebarBody />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="w-64 border-l border-gray-200 bg-white flex flex-col">
            <SidebarBody onNavClick={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-gray-200 px-4 sm:px-8 py-3 flex items-center justify-between bg-white sticky top-0 z-20">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="open-mobile-menu"
              className="lg:hidden p-1.5 -ml-1.5 text-gray-700 hover:text-[#004B87]"
            >
              <Menu size={20} />
            </button>
            <Globe2 size={14} className="text-gray-400 hidden sm:block" />
            <span className="text-gray-500 hidden sm:inline">{country}</span>
            <ChevronRight size={14} className="text-gray-300 hidden sm:block" />
            <span className="font-semibold truncate" data-testid="page-title">
              {current?.label || "Dashboard"}
            </span>
          </div>
          <CountrySelector />
        </header>

        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
